import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { RFIProvider } from './context/RFIContext';
import { FeedbackProvider } from './context/FeedbackContext';
import { RoleProvider } from './context/RoleContext';
import { PersonaProvider } from './context/PersonaContext';
import { AIAssistantProvider } from './context/AIAssistantContext';
import { AcumenProvider } from './context/AcumenContext';
import { AcumenCommandProvider } from './context/AcumenCommandContext';
import AcumenOverlay from './components/acumen/AcumenOverlay';
import AcumenActionStrip from './components/acumen/AcumenActionStrip';
import AcumenCommandBar from './components/acumen/AcumenCommandBar';
import AcumenCursorOverlay from './components/acumen/AcumenCursorOverlay';
import AcumenClickInterceptor from './components/acumen/AcumenClickInterceptor';
import { NotificationProvider } from './context/NotificationContext';
import { OfflineProvider } from './context/OfflineContext';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import ErrorBoundary from './components/ErrorBoundary';
import SkipLink from './components/SkipLink';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import RFINotificationBar from './components/RFINotificationBar';
import CommandPalette from './components/CommandPalette';
import UnsavedChangesDialog from './components/ui/UnsavedChangesDialog';
import { Toaster } from 'sonner';
// LiveActivityFeed removed - activity now integrated into Acuity AI assistant
// QuickActionsButton (FAB) removed - replaced by customizable sidebar
import AIAssistantWidget from './components/AIAssistantWidget';
import { setSentryUser, clearSentryUser } from './services/sentry';
import { getAccessToken, clearAccessToken } from './config/api';
// PWA utilities disabled to prevent flashing - can be re-enabled for production build
// import { registerServiceWorker, OfflineIndicator, UpdateBanner } from './utils/pwa';
// KeyboardShortcutsHint moved to Help Center

// Lazy-loaded pages for bundle optimization
const Dashboard = lazy(() => import('./pages/Dashboard'));
const OpportunitiesList = lazy(() => import('./pages/OpportunitiesList'));
const OpportunityExplorer = lazy(() => import('./pages/OpportunityExplorer'));
const PortfolioRadar = lazy(() => import('./pages/PortfolioRadar'));
const EvaluationRoom = lazy(() => import('./pages/EvaluationRoom'));
const SmartSubmissionPortal = lazy(() => import('./pages/SmartSubmissionPortal'));
const ModulesAndDomains = lazy(() => import('./pages/ModulesAndDomains'));
const Settings = lazy(() => import('./pages/Settings'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const VendorSubmissionPortal = lazy(() => import('./pages/VendorSubmissionPortal'));
const TeamWorkspace = lazy(() => import('./pages/TeamWorkspace'));
const ContractAnalyzer = lazy(() => import('./pages/ContractAnalyzer'));
const CWSHub = lazy(() => import('./pages/CWSHub'));
const GAOTwin = lazy(() => import('./pages/GAOTwin'));
const SolicitationBuilder = lazy(() => import('./pages/SolicitationBuilder'));
const PortfolioChat = lazy(() => import('./pages/PortfolioChat'));
const ReceiptAcceptance = lazy(() => import('./pages/ReceiptAcceptance'));
const RealPropertyLeasing = lazy(() => import('./pages/RealPropertyLeasing'));
const OrderingOfficer = lazy(() => import('./pages/OrderingOfficer'));
const AcquisitionWizard = lazy(() => import('./pages/AcquisitionWizard'));
const FPDSNgReporting = lazy(() => import('./pages/FPDSNgReporting'));
const FITARAReview = lazy(() => import('./pages/FITARAReview'));
const EvaluationNarrative = lazy(() => import('./pages/EvaluationNarrative'));
const SF30ModEngine = lazy(() => import('./pages/SF30ModEngine'));
const RatificationManagement = lazy(() => import('./pages/RatificationManagement'));
const ContractCatalog = lazy(() => import('./pages/ContractCatalog'));
const DocumentLibrary = lazy(() => import('./pages/DocumentLibrary'));
const SDVOSBTracker = lazy(() => import('./pages/SDVOSBTracker'));
const CalendarDeadlines = lazy(() => import('./pages/CalendarDeadlines'));
const RiskDashboard = lazy(() => import('./pages/RiskDashboard'));
const ComplianceChecker = lazy(() => import('./pages/ComplianceChecker'));
const MarketResearch = lazy(() => import('./pages/MarketResearch'));
const IGCEGenerator = lazy(() => import('./pages/IGCEGenerator'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const ProposalEvaluator = lazy(() => import('./pages/ProposalEvaluator'));
const AILearningCenter = lazy(() => import('./pages/AILearningCenter'));
const CollaborationRoom = lazy(() => import('./pages/CollaborationRoom'));
const MyVault = lazy(() => import('./pages/MyVault'));
const WorkspaceDocEditor = lazy(() => import('./pages/WorkspaceDocEditor'));
const DocumentCreator = lazy(() => import('./pages/DocumentCreatorNew'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const ProjectsHome = lazy(() => import('./pages/ProjectsHome'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const CMSDashboard = lazy(() => import('./pages/CMSDashboard'));
const MilestonesPage = lazy(() => import('./pages/MilestonesPage'));
const RisksPage = lazy(() => import('./pages/RisksPage'));
const SolicitationHub = lazy(() => import('./pages/SolicitationHub'));
const ConstructionLanding = lazy(() => import('./pages/ConstructionLanding'));
const VendorDatabase = lazy(() => import('./pages/VendorDatabase'));
const EvaluationWorkbench = lazy(() => import('./pages/EvaluationWorkbench'));
const AcquisitionTimeline = lazy(() => import('./pages/AcquisitionTimeline'));
const AcquisitionPipeline = lazy(() => import('./pages/AcquisitionPipeline'));
const Integrations = lazy(() => import('./pages/Integrations'));
const ClauseRecommender = lazy(() => import('./pages/ClauseRecommender'));
const EvaluationTools = lazy(() => import('./pages/EvaluationTools'));
const GovIntegrations = lazy(() => import('./pages/GovIntegrations'));
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));
const LeadershipAnalytics = lazy(() => import('./pages/LeadershipAnalytics'));
const StakeholderPortals = lazy(() => import('./pages/StakeholderPortals'));
const QuickWins = lazy(() => import('./pages/QuickWins'));
const DecisionWizard = lazy(() => import('./pages/DecisionWizard'));
const MobileApproval = lazy(() => import('./pages/MobileApproval'));
const MeetingNotes = lazy(() => import('./pages/MeetingNotes'));
const OnboardingAnalytics = lazy(() => import('./pages/OnboardingAnalytics'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const RFIWizard = lazy(() => import('./pages/RFIWizard'));
const PostPortal = lazy(() => import('./pages/PostPortal'));
const SourceSelection = lazy(() => import('./pages/SourceSelection'));
const AcquisitionPlanWizard = lazy(() => import('./pages/AcquisitionPlanWizard'));
const JustificationApproval = lazy(() => import('./pages/JustificationApproval'));
const VALibraryNew = lazy(() => import('./pages/VALibraryNew'));
// HelpWidget removed - help accessible via Help Center page
import { OfflineBanner } from './components/OfflineBanner';
import OnboardingWizard from './components/OnboardingWizard';
import './App.css';

// Use centralized API configuration
import { API_URL, API_BASE_URL, api } from './config/api';
import logger from './utils/logger';
import SynopsisGenerator from './pages/SynopsisGenerator';
import ClauseMatrix from './pages/ClauseMatrix';
const BACKEND_URL = API_BASE_URL;

// Main layout with sidebar - wrapped with AIAssistantProvider + AcumenProvider
function MainLayout({ children, title }) {
  return (
    <AcumenCommandProvider>
      <AcumenProvider>
        <AIAssistantProvider>
          <MainLayoutContent title={title}>
            {children}
          </MainLayoutContent>
          <AcumenCommandBar />
          <AcumenCursorOverlay />
          <AcumenClickInterceptor />
          <AcumenOverlay />
          <AcumenActionStrip />
        </AIAssistantProvider>
      </AcumenProvider>
    </AcumenCommandProvider>
  );
}

function MainLayoutContent({ children, title }) {
  const { darkMode } = useTheme();
  const location = useLocation();
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Navigation context for unsaved changes dialog
  const { 
    showUnsavedDialog, 
    confirmLeave, 
    cancelLeave,
    unsavedChangesState 
  } = useNavigation();
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('asp_user') || '{}');
  
  // Acuity AI visibility setting - OFF by default, user must opt-in via Settings
  const [acuityEnabled, setAcuityEnabled] = useState(() => {
    const stored = localStorage.getItem('asp_settings');
    if (stored) {
      try {
        const settings = JSON.parse(stored);
        return settings.acuityEnabled === true; // Must be explicitly true to show
      } catch (e) {
        return false;
      }
    }
    return false; // Default OFF
  });
  
  // Listen for settings changes - both cross-tab (storage) and same-tab (custom event)
  useEffect(() => {
    // Handler for cross-tab storage events
    const handleStorageChange = () => {
      const stored = localStorage.getItem('asp_settings');
      if (stored) {
        try {
          const settings = JSON.parse(stored);
          setAcuityEnabled(settings.acuityEnabled === true); // Must be explicitly true
        } catch (e) {}
      } else {
        setAcuityEnabled(false); // Default OFF if no settings
      }
    };
    
    // Handler for same-tab custom events (fired from Settings page)
    const handleAcuityChange = (event) => {
      setAcuityEnabled(event.detail.acuityEnabled === true);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('acuity-settings-changed', handleAcuityChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('acuity-settings-changed', handleAcuityChange);
    };
  }, []);
  
  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also scroll the main content container
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Global keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows/Linux) to open Command Palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K (without Shift - Shift is for Acuity AI)
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setShowCommandPalette(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  return (
    <div className={`flex h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Skip navigation link for keyboard users */}
      <SkipLink targetId="main-content" />
      
      {/* Mobile hamburger menu button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className={`fixed top-4 left-4 z-30 p-2 rounded-lg md:hidden transition-colors ${
          darkMode 
            ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-700' 
            : 'bg-white text-gray-900 hover:bg-gray-100 border border-gray-200 shadow-sm'
        }`}
        aria-label="Open navigation menu"
        data-testid="mobile-menu-btn"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      {/* Sidebar navigation - single instance handles both mobile and desktop */}
      <Sidebar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <RFINotificationBar />
        <TopBar title={title} user={user} />
        
        {/* Main content area with landmark - subtle gray background for contrast */}
        <main 
          id="main-content" 
          className={`flex-1 overflow-y-auto page-transition ${darkMode ? 'bg-slate-900' : 'bg-slate-50/80'}`}
          role="main"
          aria-label="Main content"
          tabIndex="-1"
        >
          {children}
        </main>
      </div>
      
      {/* Global UI Components */}
      <CommandPalette isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
      {/* LiveActivityFeed removed - activity now shown in Acuity AI assistant */}
      {/* QuickActionsButton (FAB) removed - customization moved to Settings > Sidebar Preferences */}
      {acuityEnabled && <AIAssistantWidget />}
      {/* HelpWidget (bottom-left FAB) removed - help accessible via Help Center page */}
      <OfflineBanner />
      {/* Shortcuts moved to Help Center */}
      <Toaster position="top-center" richColors toastOptions={{ error: { style: { display: 'none' } } }} />
      
      {/* Unsaved Changes Dialog - shown globally when navigation is blocked */}
      {showUnsavedDialog && (
        <UnsavedChangesDialog
          darkMode={darkMode}
          onStay={cancelLeave}
          onLeave={confirmLeave}
          message={unsavedChangesState?.message || "You have unsaved changes that will be lost if you leave this page."}
        />
      )}
    </div>
  );
}

function App() {
  // Service worker registration disabled to prevent flashing
  // useEffect(() => {
  //   registerServiceWorker();
  // }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <FeedbackProvider>
          <RoleProvider>
            <PersonaProvider>
              <NotificationProvider>
                <OfflineProvider>
                  <NavigationProvider>
                    <RFIProvider>
                      <BrowserRouter>
                        <AppRoutes />
                      </BrowserRouter>
                    </RFIProvider>
                  </NavigationProvider>
                </OfflineProvider>
              </NotificationProvider>
            </PersonaProvider>
          </RoleProvider>
        </FeedbackProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    // Check if user has valid JWT token
    const checkSession = async () => {
      const token = getAccessToken();
      const storedUser = localStorage.getItem('asp_user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await fetch(`${BACKEND_URL}/api/auth/verify`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'  // Include cookies for HttpOnly refresh token
          });
          
          if (response.ok) {
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          } else {
            // Token invalid, clear storage
            clearAccessToken();
          }
        } catch (e) {
          // Network error, but still allow if we have token (offline mode)
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        }
      }
      setIsChecking(false);
    };
    
    checkSession();
  }, []);

  // Check onboarding status when authenticated
  useEffect(() => {
    const checkOnboarding = async () => {
      if (isAuthenticated && !onboardingChecked) {
        try {
          const response = await api.get('/onboarding/status');
          if (response.needs_onboarding) {
            setShowOnboarding(true);
          }
        } catch (e) {
          logger.error('Failed to check onboarding status:', e);
        }
        setOnboardingChecked(true);
      }
    };
    
    checkOnboarding();
  }, [isAuthenticated, onboardingChecked]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setOnboardingChecked(false); // Reset to trigger onboarding check
    // Set Sentry user context for error tracking
    setSentryUser(userData);
  };

  const handleLogout = () => {
    clearAccessToken();
    setUser(null);
    setIsAuthenticated(false);
    setShowOnboarding(false);
    setOnboardingChecked(false);
    // Clear Sentry user context
    clearSentryUser();
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  // Show nothing while checking authentication
  if (isChecking) {
    return null;
  }

  return (
    <>
      {/* Onboarding Wizard Overlay */}
      {isAuthenticated && showOnboarding && (
        <OnboardingWizard 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
      
      <Routes>
      {/* Public routes */}
      <Route path="/construction" element={<ConstructionLanding />} />
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
      } />
      <Route path="/register" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Register />
      } />
      <Route path="/forgot-password" element={
        isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />
      } />
      <Route path="/reset-password" element={
        isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />
      } />
      
      {/* Admin route - accessible by admin and super_admin */}
      <Route path="/admin" element={
        isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin') ? <AdminPanel /> : <Navigate to="/login" replace />
      } />
      
      {/* Protected routes - redirect to login if not authenticated */}
      {!isAuthenticated ? (
        <Route path="*" element={<Navigate to="/login" replace />} />
      ) : (
        <>
          <Route path="/" element={
            <MainLayout title="Dashboard">
              <Dashboard user={user} />
            </MainLayout>
          } />
          <Route path="/opportunities" element={
            <MainLayout title="Opportunities">
              <OpportunitiesList user={user} />
            </MainLayout>
          } />
          <Route path="/opportunity-explorer" element={
            <MainLayout title="Opportunity Explorer">
              <OpportunityExplorer />
            </MainLayout>
          } />
          <Route path="/portfolio" element={
          <MainLayout title="Portfolio Radar">
            <PortfolioRadar user={user} />
          </MainLayout>
        } />
        <Route path="/analytics" element={
          <MainLayout title="Analytics Dashboard">
            <AnalyticsDashboard user={user} />
          </MainLayout>
        } />
        <Route path="/evaluation" element={
          <MainLayout title="Evaluation Room">
            <EvaluationRoom user={user} />
          </MainLayout>
        } />
        <Route path="/modules" element={
          <MainLayout title="Modules & Domains">
            <ModulesAndDomains user={user} />
          </MainLayout>
        } />
        <Route path="/submissions" element={
          <MainLayout title="Smart Submission Portal">
            <SmartSubmissionPortal user={user} />
          </MainLayout>
        } />
        <Route path="/integrations" element={
          <MainLayout title="Integrations">
            <Integrations user={user} />
          </MainLayout>
        } />
        <Route path="/settings" element={
          <MainLayout title="Settings">
            <Settings user={user} />
          </MainLayout>
        } />
        <Route path="/onboarding-analytics" element={
          <MainLayout title="Onboarding Analytics">
            <OnboardingAnalytics user={user} />
          </MainLayout>
        } />
        <Route path="/audit-log" element={
          <MainLayout title="Audit Log">
            <AuditLog user={user} />
          </MainLayout>
        } />
        <Route path="/super-admin" element={
          <MainLayout title="Super Admin">
            <SuperAdminDashboard user={user} />
          </MainLayout>
        } />
        <Route path="/help" element={
          <MainLayout title="Help Center">
            <HelpCenter user={user} />
          </MainLayout>
        } />
        <Route path="/vendor-portal" element={
          <MainLayout title="Vendor Submission Portal">
            <VendorSubmissionPortal />
          </MainLayout>
        } />
        <Route path="/team-workspace" element={
          <MainLayout title="Collaboration Room">
            <TeamWorkspace />
          </MainLayout>
        } />
        <Route path="/contract-analyzer" element={
          <MainLayout title="AI Contract Analyzer">
            <ContractAnalyzer />
          </MainLayout>
        } />
        {/* Contract Writing Suite (CWS) */}
        <Route path="/cws" element={
          <MainLayout title="Encore Contract Writing Suite">
            <CWSHub />
          </MainLayout>
        } />
        <Route path="/cws/gao-twin" element={
          <MainLayout title="GAO Twin">
            <GAOTwin />
          </MainLayout>
        } />
        <Route path="/cws/solicitation-builder" element={
          <MainLayout title="Solicitation Builder">
            <SolicitationBuilder />
          </MainLayout>
        } />
        <Route path="/cws/portfolio-chat" element={
          <MainLayout title="Chat With Your Contracts">
            <PortfolioChat />
          </MainLayout>
        } />
        <Route path="/cws/receipt-acceptance" element={
          <MainLayout title="Receipt + Acceptance Module">
            <ReceiptAcceptance />
          </MainLayout>
        } />
        <Route path="/cws/real-property-leasing" element={
          <MainLayout title="Real Property Leasing Module">
            <RealPropertyLeasing />
          </MainLayout>
        } />
        <Route path="/cws/ordering-officer" element={
          <MainLayout title="Ordering Officer Management Module">
            <OrderingOfficer />
          </MainLayout>
        } />
        <Route path="/acquisition-wizard" element={
          <MainLayout title="Acquisition Wizard">
            <AcquisitionWizard />
          </MainLayout>
        } />
        <Route path="/evaluation-narrative" element={
        <MainLayout title="Evaluation Narrative">
          <Suspense fallback={<div />}><EvaluationNarrative /></Suspense>
        </MainLayout>} />
        <Route path="/sf30-mod-engine" element={
        <MainLayout title="SF-30 Mod Engine">
          <Suspense fallback={<div />}><SF30ModEngine /></Suspense>
        </MainLayout>} />
        <Route path="/cws/fitara-review" element={
          <MainLayout title="FITARA CIO Review">
            <FITARAReview />
          </MainLayout>
        } />
        <Route path="/cws/ratification" element={
          <MainLayout title="Ratification Management">
            <RatificationManagement />
          </MainLayout>
        } />
        <Route path="/cws/contract-catalog" element={
          <MainLayout title="Contract Catalog">
            <ContractCatalog />
          </MainLayout>
        } />
        <Route path="/cws/fpds-reporting" element={
          <MainLayout title="FPDS-NG Award Reporting">
            <FPDSNgReporting />
          </MainLayout>
        } />
        <Route path="/va-library" element={
          <MainLayout title="VA Library">
            <DocumentLibrary />
          </MainLayout>
        } />
        {/* Backward compatibility: redirect old /document-library to /va-library */}
        <Route path="/document-library" element={<Navigate to="/va-library" replace />} />
        <Route path="/va-library-new" element={
          <MainLayout title="VA Library">
            <VALibraryNew />
          </MainLayout>
        } />
        <Route path="/clause-recommender" element={
          <MainLayout title="Clause Recommender">
            <ClauseRecommender />
          </MainLayout>
        } />
        <Route path="/evaluation-tools" element={
          <MainLayout title="Evaluation Tools">
            <EvaluationTools />
          </MainLayout>
        } />
        <Route path="/gov-integrations" element={
          <MainLayout title="Government Integrations">
            <GovIntegrations />
          </MainLayout>
        } />
        <Route path="/sdvosb-tracker" element={
          <MainLayout title="SDVOSB/VOSB Tracker">
            <SDVOSBTracker />
          </MainLayout>
        } />
        <Route path="/calendar" element={
          <MainLayout title="Calendar & Deadlines">
            <CalendarDeadlines />
          </MainLayout>
        } />
        <Route path="/risk-dashboard" element={
          <MainLayout title="Risk Dashboard">
            <RiskDashboard />
          </MainLayout>
        } />
        <Route path="/compliance-checker" element={
          <MainLayout title="Compliance Checker">
            <ComplianceChecker />
          </MainLayout>
        } />
        <Route path="/market-research" element={
          <MainLayout title="Market Research">
            <MarketResearch />
          </MainLayout>
        } />
        <Route path="/igce-generator" element={
          <MainLayout title="IGCE Generator">
            <IGCEGenerator />
          </MainLayout>
        } />
        <Route path="/proposal-evaluator" element={
          <MainLayout title="Proposal Evaluator">
            <ProposalEvaluator />
          </MainLayout>
        } />
        <Route path="/ai-learning" element={
          <MainLayout title="AI Learning Center">
            <AILearningCenter />
          </MainLayout>
        } />
        <Route path="/collaboration-room" element={
          <CollaborationRoom />
        } />
        {/* Primary route for Workspace Files */}
        <Route path="/workspace-files" element={
          <MainLayout title="Workspace Files">
            <MyVault />
          </MainLayout>
        } />
        {/* Workspace Files Document Editor - full page editor with returnTo support */}
        <Route path="/workspace-files/doc/:id" element={
          <WorkspaceDocEditor />
        } />
        {/* Backward-compatible redirect from old /my-vault route */}
        <Route path="/my-vault" element={<Navigate to="/workspace-files" replace />} />
        <Route path="/document-creator" element={
          <MainLayout title="Document Creator">
            <DocumentCreator />
          </MainLayout>
        } />
        {/* Super Admin Panel - only accessible by super admin */}
        <Route path="/super-admin" element={<SuperAdminPanel />} />
        {/* Projects - New clean page with mocked New Project modal */}
        <Route path="/projects" element={
          <MainLayout title="Projects">
            <ProjectsHome user={user} />
          </MainLayout>
        } />
        {/* Project Detail Page */}
        <Route path="/projects/:id" element={
          <MainLayout title="Project">
            <ProjectDetail />
          </MainLayout>
        } />
        {/* Contracts Management Page */}
        <Route path="/cms-dashboard" element={<MainLayout title="Contract Management"><Suspense fallback={<div/>}><CMSDashboard /></Suspense></MainLayout>} />
        <Route path="/contracts" element={
          <MainLayout title="Contracts">
            <ContractsPage />
          </MainLayout>
        } />
        {/* Milestones Management Page */}
        <Route path="/milestones" element={
          <MainLayout title="Milestones">
            <MilestonesPage />
          </MainLayout>
        } />
        {/* Risks Management Page */}
        <Route path="/risks" element={
          <MainLayout title="Risks">
            <RisksPage />
          </MainLayout>
        } />
        {/* Solicitation Hub - Procurement Lifecycle */}
        <Route path="/solicitation-hub" element={
          <MainLayout title="Solicitation Hub">
            <SolicitationHub />
          </MainLayout>
        } />
        {/* Vendor Database */}
        <Route path="/vendor-database" element={
          <MainLayout title="Vendor Database">
            <VendorDatabase />
          </MainLayout>
        } />
        {/* Evaluation Workbench */}
        <Route path="/evaluation-workbench" element={
          <MainLayout title="Evaluation Workbench">
            <EvaluationWorkbench />
          </MainLayout>
        } />
        {/* Acquisition Timeline */}
        <Route path="/acquisition-timeline" element={
          <MainLayout title="Acquisition Timeline">
            <AcquisitionTimeline />
          </MainLayout>
        } />
        {/* Acquisition Pipeline - Visual Lifecycle View */}
        <Route path="/acquisition-pipeline" element={
          <MainLayout title="Acquisition Pipeline">
            <AcquisitionPipeline />
          </MainLayout>
        } />
        {/* Audit Trail Dashboard - Compliance & Protest Defense */}
        <Route path="/audit-dashboard" element={
          <MainLayout title="Audit Trail">
            <AuditDashboard />
          </MainLayout>
        } />
        {/* Leadership Analytics Dashboard */}
        <Route path="/leadership-analytics" element={
          <MainLayout title="Leadership Analytics">
            <LeadershipAnalytics />
          </MainLayout>
        } />
        {/* Stakeholder Portals - Cross-functional Collaboration */}
        <Route path="/stakeholder-portals" element={
          <MainLayout title="Stakeholder Portals">
            <StakeholderPortals />
          </MainLayout>
        } />
        {/* Quick Wins - Productivity Tools */}
        <Route path="/quick-wins" element={
          <MainLayout title="Quick Wins">
            <QuickWins />
          </MainLayout>
        } />
        {/* Decision Support Wizard */}
        <Route path="/decision-wizard" element={
          <MainLayout title="Decision Wizard">
            <DecisionWizard />
          </MainLayout>
        } />
        {/* Mobile Approval Center - Optimized for phones */}
        <Route path="/mobile-approval" element={
          <MobileApproval />
        } />
        <Route path="/meeting-notes" element={
          <MainLayout title="Meeting Notes">
            <MeetingNotes />
          </MainLayout>
        } />
        <Route path="/rfi-wizard" element={
          <MainLayout title="Test RFI">
            <RFIWizard />
          </MainLayout>
        } />
        <Route path="/post-portal" element={
          <MainLayout title="Post Portal">
            <PostPortal />
          </MainLayout>
        } />
        {/* Acquisition Plan Wizard */}
        <Route path="/acquisition-plan" element={
          <MainLayout title="Acquisition Plan Wizard">
            <AcquisitionPlanWizard />
          </MainLayout>
        } />
        {/* Justification & Approval Builder */}
        <Route path="/justification-approval" element={
          <MainLayout title="Justification & Approval">
            <JustificationApproval />
          </MainLayout>
        } />
        {/* Redirect any unknown routes to dashboard */}
        <Route path="/source-selection" element={
          <MainLayout title="Source Selection">
            <SourceSelection />
          </MainLayout>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
        <Route path="/synopsis-generator" element={
          <MainLayout title="Synopsis Generator">
            <SynopsisGenerator />
          </MainLayout>
        } />
        <Route path="/clause-matrix" element={
          <MainLayout title="Clause Matrix">
            <ClauseMatrix />
          </MainLayout>
        } />
              </Routes>
    </>
  );
}

export default App;